import { KeyboardArrowDown } from "@mui/icons-material"
import { Box, Card, Container, ListItemButton, Stack, Typography, styled } from "@mui/material"
import type { CardProps } from "@mui/material"
import { useActiveFeatureFlags } from "posthog-js/react"
import { useTranslation } from "react-i18next"

import { NavLink, StyledNavLink } from "./components/NavLink"
import navbarNavigations from "./config"
import type { NavbarNavigationType } from "./config"

const ChildNavsWrapper = styled(Box)({
  zIndex: 10,
  left: "50%",
  top: "100%",
  display: "none",
  position: "absolute",
  transform: "translate(-50%, 0%)",
})

interface StyledCardProps extends CardProps {
  hoverEffect?: boolean
}

const StyledCard = styled(Card)<StyledCardProps>(({ theme, hoverEffect }) => ({
  zIndex: 10,
  overflow: "unset",
  borderRadius: "8px",
  transition: "all 250ms ease-in-out",
  boxShadow: theme.customShadows.z4,
  "&:hover": {
    ...(hoverEffect && {
      boxShadow: theme.customShadows.z8,
    }),
  },
}))

interface NavBarWrapperProps extends CardProps {
  border?: boolean
}

const NavBarWrapper = styled(StyledCard, {
  shouldForwardProp: (prop) => prop !== "border",
})<NavBarWrapperProps>(({ theme, border }) => ({
  height: "60px",
  display: "block",
  borderRadius: "0px",
  position: "relative",
  ...(border && {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  }),
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}))

const InnerContainer = styled(Container)({
  height: "100%",
  display: "flex",
  alignItems: "center",
})

function Navbar() {
  const { t } = useTranslation("common", { keyPrefix: "navbar" })
  const features = useActiveFeatureFlags()

  const renderItems = (list: NavbarNavigationType[] = []) => {
    return list.map((nav) => {
      if (nav.url) {
        return (
          <StyledNavLink to={nav.url} key={nav.title}>
            {t(nav.title)}
          </StyledNavLink>
        )
      }
      if (nav.child) {
        return (
          <Stack
            key={nav.title}
            direction="column"
            sx={{
              alignItems: "center",
              position: "relative",
              "&:hover": {
                "& > .child-nav-item": {
                  display: "block",
                },
              },
            }}
          >
            <Stack
              direction="row"
              spacing={0.3}
              sx={{
                alignItems: "center",
              }}
            >
              <Typography>{t(nav.title)}</Typography>
              <KeyboardArrowDown
                sx={{
                  color: "inherit",
                  fontSize: "1.1rem",
                }}
              />
            </Stack>
            <ChildNavsWrapper className="child-nav-item">
              <StyledCard
                elevation={3}
                sx={{
                  mt: 2.5,
                  py: 1,
                  minWidth: 200,
                }}
              >
                {nav.child.map((child) => {
                  if (
                    child.featureFlag &&
                    features !== undefined &&
                    !features.includes(child.featureFlag)
                  ) {
                    return null
                  }

                  return (
                    <NavLink to={child.url} key={child.title}>
                      <ListItemButton
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 0.75,
                          color: "inherit",
                        }}
                      >
                        {t(child.title)}
                      </ListItemButton>
                    </NavLink>
                  )
                })}
              </StyledCard>
            </ChildNavsWrapper>
          </Stack>
        )
      }
    })
  }

  return (
    <NavBarWrapper elevation={0} border={true}>
      <InnerContainer maxWidth="lg">
        <Stack
          direction="row"
          spacing={4}
          sx={{
            paddingLeft: 2,
          }}
        >
          {renderItems(navbarNavigations)}
        </Stack>
      </InnerContainer>
    </NavBarWrapper>
  )
}

export default Navbar
